import { CollapsePanelExtendedProps } from '@hellodarwin/core/lib/components/common/Collapse/CollapsePanel';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import { PreviewProjectResponse } from '@hellodarwin/core/lib/features/entities';
import Chevron from '@hellodarwin/icons/dist/icons/Chevron';
import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/index';
import {
  selectCompany,
  selectSubsidiaryIds,
} from '../../../features/api/slices/profile-slice';
import {
  fetchAllProjectsPreview,
  selectAllProjectsPreview,
  selectProjectsIsLoading,
} from '../../../features/api/slices/projects-slice';
import { useNewClientApi } from '../../../features/api/use-client-api';
import PriorityTile from '../priority-tile';
import EmptyProjects from './empty-projects';
import { ProjectsTableContent, SubsidiaryCollapse } from './styles';

export interface ProjectsTableProps {}

interface ProjectGroup {
  companyId: string;
  companyName: string;
  projects: PreviewProjectResponse[];
}

const ChevronExpandIcon = ({ isActive }: CollapsePanelExtendedProps) => (
  <Chevron direction={isActive ? 'down' : 'right'} size={32} />
);

const ProjectsTable: React.FC<ProjectsTableProps> = () => {
  const api = useNewClientApi();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const projectsPreview = useAppSelector(selectAllProjectsPreview);
  const isLoading = useAppSelector((state) =>
    selectProjectsIsLoading(state, 'allProjects'),
  );

  const subsidiaryIds = useAppSelector(selectSubsidiaryIds);
  const hasSubsidiaries = subsidiaryIds.length > 0;

  useEffect(() => {
    dispatch(fetchAllProjectsPreview({ api }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projectGroups = useMemo<ProjectGroup[]>(() => {
    if (!hasSubsidiaries || !projectsPreview.length) return [];

    const groupMap = new Map<string, ProjectGroup>();

    for (const project of projectsPreview) {
      // Use program_company_ids to assign the project to every relevant subsidiary group
      const companyIds = project.program_company_ids?.length
        ? project.program_company_ids
        : [project.company_id];

      for (const cid of companyIds) {
        if (!groupMap.has(cid)) {
          groupMap.set(cid, {
            companyId: cid,
            companyName: project.program_company_names?.[cid] || '',
            projects: [],
          });
        }
        groupMap.get(cid)!.projects.push(project);
      }
    }

    // Main company first, then subsidiaries alphabetically
    const groups = Array.from(groupMap.values());
    groups.sort((a, b) => {
      if (a.companyId === company.company_id) return -1;
      if (b.companyId === company.company_id) return 1;
      return a.companyName.localeCompare(b.companyName);
    });

    return groups;
  }, [projectsPreview, hasSubsidiaries, company.company_id]);

  if (isLoading) {
    return <Loading />;
  }

  if (!projectsPreview.length) {
    return <EmptyProjects />;
  }

  if (!hasSubsidiaries || projectGroups.length === 1) {
    return (
      <ProjectsTableContent>
        {projectsPreview.map((p) => (
          <PriorityTile key={p.project_id} priority={p} />
        ))}
      </ProjectsTableContent>
    );
  }

  return (
    <SubsidiaryCollapse
      defaultActiveKey={projectGroups.map((g) => g.companyId)}
      gap={16}
      expandIconPosition="start"
      ExpandIcon={ChevronExpandIcon}
      items={projectGroups.map((group) => ({
        id: group.companyId,
        title: (
          <Typography elementTheme="subtitle2">{group.companyName}</Typography>
        ),
        children: (
          <ProjectsTableContent>
            {group.projects.map((p) => (
              <PriorityTile key={p.project_id} priority={p} />
            ))}
          </ProjectsTableContent>
        ),
      }))}
    />
  );
};
export default ProjectsTable;
